import { useEffect, useState, useCallback } from 'react';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina, FaixaFiltros } from '../components/layout/CabecalhoPagina';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { adminApi, teamApi, beltApi, userApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Users, UserPlus, Search, Loader2, Edit, KeyRound, Trash2, ShieldAlert,
  ChevronLeft, ChevronRight, Mail, Shield, AlertTriangle, IdCard,
} from 'lucide-react';
import PersonProfileFields, { emptyPersonProfile } from '../components/PersonProfileFields';
// FASE E — o campo único de documento vira `document` + `cpf` ou `rg` no envio
import { comCamposDeDocumento, documentoDoRegistro, tipoDoRegistro } from '../lib/documentos';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  federation: 'Federação',
  team: 'Equipe',
  professor: 'Professor',
  staff: 'Staff',
  central_desk: 'Mesa Central',
  athlete: 'Atleta',
  public: 'Público',
};

const ROLE_COLORS = {
  super_admin: 'bg-red-500/15 text-red-300 border-red-500/40',
  federation: 'bg-purple-500/15 text-purple-300 border-purple-500/40',
  team: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  professor: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  staff: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40',
  central_desk: 'bg-pink-500/15 text-pink-300 border-pink-500/40',
  athlete: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  public: 'bg-gray-500/15 text-gray-300 border-gray-500/40',
};

const EMPTY_FORM = { name: '', email: '', role: 'athlete', team_id: '', password: '' };
const PAGE_LIMIT = 25;

export default function UsersAdminPage() {
  const { user: currentUser } = useAuth();
  const isAuthorized = currentUser?.role === 'super_admin';

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Reference data for the profile fields (teams, professors, belts)
  const [teams, setTeams] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [belts, setBelts] = useState([]);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [createProfile, setCreateProfile] = useState(emptyPersonProfile());
  const [createBusy, setCreateBusy] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editProfile, setEditProfile] = useState(emptyPersonProfile());
  // FASE 2 — guarda a faixa ORIGINAL: é ela que deixa o formulário perceber que
  // a faixa foi trocada e perguntar se a data de hoje vira a nova "desde quando".
  const [editBeltOriginal, setEditBeltOriginal] = useState('');
  const [editLinks, setEditLinks] = useState(null);
  const [editPwd, setEditPwd] = useState('');
  const [editPwdConfirm, setEditPwdConfirm] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLinks, setDeleteLinks] = useState(null);
  const [transferTo, setTransferTo] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.listUsers({
        q: search || undefined,
        role: roleFilter,
        page,
        limit: PAGE_LIMIT,
      });
      setUsers(data.users || []);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
      setStats(data.stats || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, page]);

  useEffect(() => {
    if (isAuthorized) fetchUsers();
  }, [isAuthorized, fetchUsers]);

  // Load reference data once (teams + professors + belts) so the profile fields work
  useEffect(() => {
    if (!isAuthorized) return;
    Promise.all([
      teamApi.list().catch(() => ({ data: [] })),
      userApi.list({ role: 'professor' }).catch(() => ({ data: [] })),
      beltApi.list().catch(() => ({ data: [] })),
    ]).then(([t, p, b]) => {
      setTeams(t.data || []);
      setProfessors(p.data || []);
      setBelts(b.data || []);
    });
  }, [isAuthorized]);

  if (!isAuthorized) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <ShieldAlert className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Acesso negado</h2>
          <p className="text-[#A1A1AA]">Apenas Super Admin pode gerenciar usuários.</p>
        </div>
      </MainLayout>
    );
  }

  // ---------- Create ----------
  const submitCreate = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Preencha nome, email e senha');
      return;
    }
    setCreateBusy(true);
    try {
      const includeProfile = ['professor', 'athlete'].includes(createForm.role);
      const payload = {
        name: createForm.name,
        email: createForm.email,
        password: createForm.password,
        role: createForm.role,
        team_id: createForm.team_id || null,
      };
      if (includeProfile) {
        payload.profile = comCamposDeDocumento(createProfile);
      }
      await adminApi.createUser(payload);
      toast.success('Usuário criado');
      setCreateOpen(false);
      setCreateForm(EMPTY_FORM);
      setCreateProfile(emptyPersonProfile());
      fetchUsers();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao criar');
    } finally {
      setCreateBusy(false);
    }
  };

  // ---------- Edit ----------
  const openEdit = async (u) => {
    setEditTarget(u);
    setEditForm({ name: u.name || '', email: u.email || '', role: u.role || 'athlete', team_id: u.team_id || '', password: '' });
    setEditPwd('');
    setEditPwdConfirm('');
    setEditLinks(null);
    setEditProfile(emptyPersonProfile());
    setEditBeltOriginal('');
    setEditOpen(true);
    try {
      const [linksRes, profileRes] = await Promise.all([
        adminApi.getUserLinks(u._id),
        adminApi.getUserProfile(u._id).catch(() => ({ data: null })),
      ]);
      setEditLinks(linksRes.data);
      if (profileRes.data) {
        const p = profileRes.data;
        setEditBeltOriginal(p.belt || '');
        setEditProfile({
          ...emptyPersonProfile(),
          ...p,
          // FASE E — o campo único abre com o documento já gravado
          documento: documentoDoRegistro(p),
          documento_tipo: tipoDoRegistro(p),
          weight: p.weight ?? '',
          height_cm: p.height_cm ?? '',
        });
      }
    } catch {
      setEditLinks({});
    }
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    setEditBusy(true);
    try {
      const includeProfile = ['professor', 'athlete'].includes(editForm.role);
      const payload = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        team_id: editForm.team_id || null,
      };
      if (includeProfile) {
        payload.profile = comCamposDeDocumento(editProfile);
      }
      await adminApi.updateUser(editTarget._id, payload);
      toast.success('Usuário atualizado');
      setEditOpen(false);
      fetchUsers();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao atualizar');
    } finally {
      setEditBusy(false);
    }
  };

  const submitPasswordReset = async () => {
    if (!editTarget) return;
    if (!editPwd) {
      toast.error('Digite a nova senha');
      return;
    }
    if (editPwd !== editPwdConfirm) {
      toast.error('Senhas não conferem');
      return;
    }
    setEditBusy(true);
    try {
      await adminApi.resetUserPassword(editTarget._id, editPwd);
      toast.success('Senha redefinida');
      setEditPwd('');
      setEditPwdConfirm('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setEditBusy(false);
    }
  };

  // ---------- Delete ----------
  const openDelete = async (u) => {
    setDeleteTarget(u);
    setTransferTo('');
    setDeleteLinks(null);
    setDeleteOpen(true);
    try {
      const { data } = await adminApi.getUserLinks(u._id);
      setDeleteLinks(data);
    } catch {
      setDeleteLinks({});
    }
  };

  const hasAnyLink = (l) =>
    l && (l.athletes_as_self?.length || l.athletes_as_professor?.length || l.teams_as_president?.length || l.teams_as_professor?.length);

  const submitDelete = async (mode) => {
    // mode: "transfer" | "force"
    if (!deleteTarget) return;
    if (mode === 'transfer' && !transferTo) {
      toast.error('Selecione o usuário destino');
      return;
    }
    setDeleteBusy(true);
    try {
      const params = mode === 'transfer' ? { transfer_to: transferTo } : { force: true };
      await adminApi.deleteUser(deleteTarget._id, params);
      toast.success(mode === 'transfer' ? 'Usuário excluído e vínculos transferidos' : 'Usuário excluído (vínculos removidos)');
      setDeleteOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao excluir');
    } finally {
      setDeleteBusy(false);
    }
  };

  // ---------- Render ----------
  return (
    <MainLayout>
      <div className="space-y-6" data-testid="users-admin-page">
        <CabecalhoPagina
          contexto="Administração"
          titulo="Gerenciamento de Usuários"
          descricao="Suporte completo: crie, edite, redefina senha e exclua usuários."
          icone={Users}
          acoes={
            <Button
              data-testid="user-create-btn"
              onClick={() => { setCreateForm(EMPTY_FORM); setCreateOpen(true); }}
              className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Novo Usuário
            </Button>
          }
        />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <StatCard label="Total" value={stats.__total__ ?? 0} highlight />
          {Object.keys(ROLE_LABELS).map((r) => (
            <StatCard key={r} label={ROLE_LABELS[r]} value={stats[r] ?? 0} />
          ))}
        </div>

        {/* Filtros em faixa própria (Etapa Visual 2) */}
        <FaixaFiltros rotulo="Buscar e filtrar">
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1 max-w-sm">
            <Label className="text-[#A1A1AA] text-xs">Buscar</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <Input
                data-testid="user-search-input"
                placeholder="Nome ou email..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 bg-[#141414] border-[#27272A] text-white"
              />
            </div>
          </div>
          <div className="w-48">
            <Label className="text-[#A1A1AA] text-xs">Role</Label>
            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="user-role-filter" className="bg-[#141414] border-[#27272A] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="all" className="text-white hover:bg-[#27272A]">Todos</SelectItem>
                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k} className="text-white hover:bg-[#27272A]">{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        </FaixaFiltros>

        {/* Table */}
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
            ) : users.length === 0 ? (
              <div className="py-20 text-center text-[#A1A1AA]">Nenhum usuário encontrado</div>
            ) : (
              <table className="tabela w-full">
                <thead className="bg-[#0A0A0A] border-b border-[#27272A]">
                  <tr>
                    <Th>Nome</Th><Th>Email</Th><Th>Role</Th><Th>Origem</Th><Th className="text-right pr-6">Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id} className="border-b border-[#27272A] hover:bg-[#1A1A1A]/50 transition-colors" data-testid={`user-row-${u._id}`}>
                      <td className="px-4 py-3 text-white font-medium">{u.name}</td>
                      <td className="px-4 py-3 text-[#A1A1AA] text-sm">{u.email}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`${ROLE_COLORS[u.role] || ROLE_COLORS.public} text-xs font-semibold uppercase`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#A1A1AA]">
                        {u.auth_provider === 'google' ? '🟢 Google' : '🔵 Local'}
                      </td>
                      <td className="px-4 py-3 text-right pr-6">
                        <div className="inline-flex gap-1">
                          <IconBtn onClick={() => openEdit(u)} testid={`user-edit-${u._id}`} title="Editar"><Edit className="w-4 h-4" /></IconBtn>
                          <IconBtn onClick={() => openDelete(u)} testid={`user-delete-${u._id}`} title="Excluir" danger><Trash2 className="w-4 h-4" /></IconBtn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between text-sm text-[#A1A1AA]">
            <span>Total: {total} usuários</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="border-[#27272A] text-white" aria-label="Página anterior">
                <ChevronLeft className="w-4 h-4" aria-hidden="true" />
              </Button>
              <span className="px-3">{page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page === pages} onClick={() => setPage((p) => p + 1)} className="border-[#27272A] text-white" aria-label="Próxima página">
                <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#141414] border-[#27272A] w-[95vw] sm:max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="user-create-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#27272A]">
            <DialogTitle className="text-white">Novo Usuário</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Crie um usuário com qualquer role.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            <div className="space-y-3">
              <Field label="Nome">
                <Input data-testid="create-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="bg-[#0A0A0A] border-[#27272A] text-white" />
              </Field>
              <Field label="Email">
                <Input data-testid="create-email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="bg-[#0A0A0A] border-[#27272A] text-white" />
              </Field>
              <Field label="Senha">
                <Input data-testid="create-password" type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className="bg-[#0A0A0A] border-[#27272A] text-white" />
              </Field>
              <Field label="Role">
                <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                  <SelectTrigger data-testid="create-role" className="bg-[#0A0A0A] border-[#27272A] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    {Object.entries(ROLE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k} className="text-white hover:bg-[#27272A]">{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {['professor', 'athlete'].includes(createForm.role) && (
              <div className="pt-3 border-t border-[#27272A]">
                <div className="flex items-center gap-2 mb-3 text-[#D4AF37]">
                  <IdCard size={16} />
                  <span className="text-xs uppercase tracking-wider font-bold">Perfil pessoal (mesmos campos do atleta)</span>
                </div>
                <PersonProfileFields formData={createProfile} setFormData={setCreateProfile} teams={teams} professors={professors} belts={belts} />
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-[#27272A] bg-[#141414] shrink-0">
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createBusy} className="border-[#27272A] text-white">Cancelar</Button>
            <Button data-testid="create-submit" onClick={submitCreate} disabled={createBusy} className="bg-[#D4AF37] hover:bg-[#B8941F] text-black">
              {createBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#141414] border-[#27272A] w-[95vw] sm:max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="user-edit-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#27272A]">
            <DialogTitle className="text-white">Editar — {editTarget?.name}</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="dados" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="bg-[#0A0A0A] border border-[#27272A] mx-6 mt-3 w-fit">
              <TabsTrigger value="dados" data-testid="tab-dados">Dados</TabsTrigger>
              {['professor', 'athlete'].includes(editForm.role) && (
                <TabsTrigger value="perfil" data-testid="tab-perfil">Perfil</TabsTrigger>
              )}
              <TabsTrigger value="senha" data-testid="tab-senha">Senha</TabsTrigger>
              <TabsTrigger value="vinculos" data-testid="tab-vinculos">Vínculos</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto px-6 pt-3 pb-4">
              <TabsContent value="dados" className="space-y-3">
                <Field label="Nome">
                  <Input data-testid="edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="bg-[#0A0A0A] border-[#27272A] text-white" />
                </Field>
                <Field label="Email">
                  <Input data-testid="edit-email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="bg-[#0A0A0A] border-[#27272A] text-white" />
                </Field>
                <Field label="Role">
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger data-testid="edit-role" className="bg-[#0A0A0A] border-[#27272A] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      {Object.entries(ROLE_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k} className="text-white hover:bg-[#27272A]">{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </TabsContent>

              <TabsContent value="perfil" className="space-y-3" data-testid="edit-perfil-content">
                <p className="text-xs text-[#A1A1AA] flex items-start gap-2">
                  <IdCard className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#D4AF37]" />
                  Estes campos são os mesmos do perfil de Atleta. Salvar atualiza o perfil vinculado.
                </p>
                <PersonProfileFields formData={editProfile} setFormData={setEditProfile} teams={teams} professors={professors} belts={belts} beltOriginal={editBeltOriginal} />
              </TabsContent>

              <TabsContent value="senha" className="space-y-3">
                <p className="text-xs text-amber-400 flex items-start gap-2">
                  <KeyRound className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Defina a nova senha aqui e informe o usuário &quot;por fora&quot; (WhatsApp, presencial, etc.).
                </p>
                <Field label="Nova senha">
                  <Input data-testid="edit-pwd" type="password" value={editPwd} onChange={(e) => setEditPwd(e.target.value)} className="bg-[#0A0A0A] border-[#27272A] text-white" />
                </Field>
                <Field label="Confirmar senha">
                  <Input data-testid="edit-pwd-confirm" type="password" value={editPwdConfirm} onChange={(e) => setEditPwdConfirm(e.target.value)} className="bg-[#0A0A0A] border-[#27272A] text-white" />
                </Field>
                <div className="pt-1">
                  <Button data-testid="edit-pwd-submit" onClick={submitPasswordReset} disabled={editBusy} className="bg-amber-500 hover:bg-amber-600 text-black">
                    {editBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                    Redefinir senha
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="vinculos" className="space-y-2 text-sm" data-testid="edit-vinculos-content">
                {!editLinks ? (
                  <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" />
                ) : (
                  <LinkSummary links={editLinks} />
                )}
              </TabsContent>
            </div>
          </Tabs>
          <DialogFooter className="px-6 py-3 border-t border-[#27272A] bg-[#141414] shrink-0">
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editBusy} className="border-[#27272A] text-white">Cancelar</Button>
            <Button data-testid="edit-submit" onClick={submitEdit} disabled={editBusy} className="bg-[#D4AF37] hover:bg-[#B8941F] text-black">
              {editBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-[#141414] border-red-900/60 max-w-lg flex flex-col" data-testid="user-delete-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-red-400 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Excluir {deleteTarget?.name}?</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Ação irreversível. Veja os vínculos abaixo antes de prosseguir.</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0">
          {!deleteLinks ? (
            <><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></>
          ) : (
            <div className="space-y-4">
              <LinkSummary links={deleteLinks} />

              {hasAnyLink(deleteLinks) ? (
                <>
                  <div className="p-3 rounded-lg border border-[#27272A] bg-[#0A0A0A] space-y-2">
                    <Label className="text-white text-xs">Opção 1 — Transferir vínculos para outro usuário:</Label>
                    <Select value={transferTo} onValueChange={setTransferTo}>
                      <SelectTrigger data-testid="delete-transfer-select" className="bg-[#141414] border-[#27272A] text-white">
                        <SelectValue placeholder="Selecione usuário destino..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A] max-h-72">
                        {users
                          .filter((u) => u._id !== deleteTarget?._id && ['professor', 'team', 'federation', 'super_admin'].includes(u.role))
                          .map((u) => (
                            <SelectItem key={u._id} value={u._id} className="text-white hover:bg-[#27272A]">
                              {u.name} · {ROLE_LABELS[u.role]}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button
                      data-testid="delete-transfer-btn"
                      onClick={() => submitDelete('transfer')}
                      disabled={deleteBusy || !transferTo}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {deleteBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Transferir vínculos e excluir
                    </Button>
                  </div>

                  <div className="p-3 rounded-lg border border-red-900/40 bg-red-950/20 space-y-2">
                    <Label className="text-red-400 text-xs">Opção 2 — Forçar exclusão (vínculos ficarão órfãos):</Label>
                    <Button
                      data-testid="delete-force-btn"
                      onClick={() => submitDelete('force')}
                      disabled={deleteBusy}
                      variant="destructive"
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Forçar exclusão
                    </Button>
                  </div>
                </>
              ) : (
                <Button
                  data-testid="delete-simple-btn"
                  onClick={() => submitDelete('force')}
                  disabled={deleteBusy}
                  variant="destructive"
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  {deleteBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Excluir usuário
                </Button>
              )}
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}

// ---------- Sub components ----------
function StatCard({ label, value, highlight }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? 'border-[#D4AF37]/40 bg-[#D4AF37]/5' : 'border-[#27272A] bg-[#141414]'}`}>
      <p className="micro-rotulo truncate">{label}</p>
      <p className={`text-2xl font-bold ${highlight ? 'text-[#D4AF37]' : 'text-white'}`}>{value}</p>
    </div>
  );
}

function Th({ children, className = '' }) {
  return <th className={`px-4 py-3 text-left micro-rotulo ${className}`}>{children}</th>;
}

function IconBtn({ children, onClick, title, testid, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
        danger ? 'text-red-400 hover:bg-red-500/10' : 'text-[#A1A1AA] hover:bg-[#27272A] hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#A1A1AA] text-xs">{label}</Label>
      {children}
    </div>
  );
}

function LinkSummary({ links }) {
  const groups = [
    { key: 'athletes_as_self', icon: Mail, label: 'Atletas (própria conta)' },
    { key: 'athletes_as_professor', icon: Shield, label: 'Alunos onde é Professor' },
    { key: 'teams_as_president', icon: Shield, label: 'Equipes onde é Presidente' },
    { key: 'teams_as_professor', icon: Shield, label: 'Equipes onde é Professor' },
  ];
  const anything = groups.some((g) => (links[g.key] || []).length > 0);
  if (!anything) {
    return <p className="text-emerald-400 text-sm">Sem vínculos. Pode excluir tranquilamente.</p>;
  }
  return (
    <div className="space-y-2">
      {groups.map(({ key, icon: Icon, label }) => {
        const items = links[key] || [];
        if (items.length === 0) return null;
        return (
          <div key={key} className="rounded-md border border-[#27272A] bg-[#0A0A0A] p-2.5">
            <div className="flex items-center gap-2 mb-1">
              <Icon className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-white text-sm font-semibold">{label}</span>
              <span className="text-xs text-[#A1A1AA]">({items.length})</span>
            </div>
            <p className="text-xs text-[#A1A1AA] pl-6 line-clamp-2">
              {items.slice(0, 5).map((i) => i.name).join(' · ')}{items.length > 5 ? ` … +${items.length - 5}` : ''}
            </p>
          </div>
        );
      })}
    </div>
  );
}
